'use client'

import { useContext } from 'react'
import { Users, ChevronsUpDown, Plus, Loader2 } from 'lucide-react'
import Image from 'next/image'
import logo from '@/images/logo.svg'
import * as Icons from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { TeamContext } from '@/contexts/TeamContext'
import { useRouter } from 'next/navigation'
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/shadcn/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'

export default function TeamSelector() {
  const authContext = useContext(AuthContext)
  const teamContext = useContext(TeamContext)
  const router = useRouter()
  const { isMobile } = useSidebar()

  if (!teamContext || !authContext.user?.organization) return null

  const { activeTeamId, teams, loading } = teamContext
  const orgName = authContext.user.organization.name
  const activeTeam = teams.find(t => t.id === activeTeamId)
  const displayName = activeTeam ? activeTeam.name : orgName
  const isOrgContext = !activeTeamId
  const canCreateTeams = authContext.user?.isAuthorized?.('createTeams')

  const getTeamIcon = team => {
    if (team?.icon && Icons[team.icon]) {
      const IC = Icons[team.icon]
      return <IC className="size-4 shrink-0" />
    }
    return <Users className="size-4 shrink-0" />
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div
                className="flex aspect-square size-8 items-center justify-center rounded-lg text-white"
                style={{
                  backgroundColor:
                    activeTeam?.color || 'hsl(var(--sidebar-primary))',
                }}
              >
                {activeTeam ? (
                  getTeamIcon(activeTeam)
                ) : (
                  <Image src={logo} alt="" className="size-4 invert" />
                )}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{displayName}</span>
                <span className="truncate text-xs">
                  {isOrgContext ? 'Organization' : 'Team'}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Organization
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => teamContext.setActiveTeam(null)}
              className="gap-2 p-2"
            >
              <div className="flex size-6 items-center justify-center rounded-sm bg-sidebar-primary">
                <Image src={logo} alt="" className="size-3.5 invert" />
              </div>
              {orgName}
            </DropdownMenuItem>

            {teams.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Teams
                </DropdownMenuLabel>
              </>
            )}

            {loading && (
              <div className="flex justify-center py-3">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading &&
              teams.map(team => (
                <DropdownMenuItem
                  key={team.id}
                  onClick={() => teamContext.setActiveTeam(team.id)}
                  className="gap-2 p-2"
                >
                  <div
                    className="flex size-6 items-center justify-center rounded-sm text-white"
                    style={{ backgroundColor: team.color || '#6b7280' }}
                  >
                    {team.icon && Icons[team.icon] ? (
                      (() => {
                        const IC = Icons[team.icon]
                        return <IC className="size-3.5 shrink-0" />
                      })()
                    ) : (
                      <Users className="size-3.5 shrink-0" />
                    )}
                  </div>
                  {team.name}
                </DropdownMenuItem>
              ))}

            {canCreateTeams && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => router.push('/manager/settings?tab=teams')}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                    <Plus className="size-4" />
                  </div>
                  <div className="font-medium text-muted-foreground">
                    Create team
                  </div>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
